import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React, { type ReactNode } from 'react';
import { ColorHex } from '../../constants';
import { TextButton } from '../Typography';

export interface SelectButtonProps {
  label?: string;
  rightIcon?: React.ReactElement<IconProps>;
  children?: ReactNode;
  onClick?: () => void;
  isActive?: boolean;
  flipArrow?: boolean;
  style?: React.CSSProperties;
  isFullWidth?: boolean;
  disabled?: boolean;
}

export const SelectButton = React.forwardRef<
  HTMLButtonElement,
  SelectButtonProps
>(
  (
    {
      label,
      rightIcon,
      children,
      onClick,
      isActive,
      flipArrow,
      style = {},
      isFullWidth = false,
      disabled,
    }: SelectButtonProps,
    ref
  ) => {
    const iconColor = isActive ? ColorHex.primary : ColorHex.natural;

    return (
      <button
        disabled={disabled}
        ref={ref}
        style={{ ...style }}
        className={classNames([
          'flex items-center justify-between rounded-md border border-solid border-ds-natural-100 hover:border-ds-natural-150 py-1 px-2.5 w-ds-47 h-ds-7.5 cursor-pointer',
          isActive && 'border-ds-primary',
          isFullWidth && 'w-ds-66',
          disabled &&
            'bg-ds-natural-200-a opacity-50 pointer-events-none text-ds-natural-200',
          !disabled && 'bg-transparent',
        ])}
        onClick={onClick && onClick}
      >
        {children}
        {label && (
          <div className="hover:text-ds-natural-300 truncate">
            <TextButton
              color="text-current"
              label={label}
            />
          </div>
        )}
        {rightIcon && (
          <div
            className={classNames(
              'ml-auto transition flex',
              flipArrow && isActive ? 'rotate-180' : 'rotate-0'
            )}
          >
            {React.cloneElement(rightIcon, {
              size: 20,
              color: iconColor,
            })}
          </div>
        )}
      </button>
    );
  }
);

SelectButton.displayName = 'SelectButton';
